import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import {
  ReentryChoiceState,
  ReentryState,
} from "@redotech/redo-return-ui/flows/chat-flow/reentry";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import {
  Button,
  ButtonSize,
  ButtonTheme,
  IconButton,
} from "@redotech/redo-web/button";
import { Divider } from "@redotech/redo-web/divider";
import LeftArrowIcon from "@redotech/redo-web/icon-old/left-arrow.svg";
import TrashIcon from "@redotech/redo-web/icon-old/trash.svg";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { InputTheme, TextInput } from "@redotech/redo-web/text-input";
import { produce } from "immer";
import { memo } from "react";
import * as multipleChoiceCss from "../return-flow/multiple-choice.module.css";
import { RequiredMessage } from "../return-flow/required-message";

export const ReentryDetails = memo(function ReentryDetails({
  stepSelect,
  state,
  setState,
}: StepTypeDetailsProps<ReentryState>) {
  // TODO: Not sure what the right permission mix is
  const canEditSupportSettings = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const canEditSettings = canEditSupportSettings || canEditReturnsSettings;

  const handleAdd = useHandler(() =>
    setState((state) => ({
      ...state,
      choices: [...state.choices, { name: "", next: undefined }],
    })),
  );
  const handleFocus = useHandler((focused: boolean) => {
    if (!focused) {
      setState((state) => ({ ...state, attempted: true }));
    }
  });

  return (
    <>
      <IterableMap items={state.choices} keyFn={(_, index) => index}>
        {(choice, index) => (
          <StateChoiceComponent
            canEditSettings={canEditSettings}
            choice={choice}
            handleFocus={handleFocus}
            index={index}
            key={index}
            setState={setState}
            state={state}
            stepSelect={stepSelect}
          />
        )}
      </IterableMap>
      <Divider />
      <div>
        <Button
          disabled={!canEditSettings}
          onClick={handleAdd}
          size={ButtonSize.SMALL}
          theme={ButtonTheme.OUTLINED}
        >
          + Add option
        </Button>
      </div>
      {!state.choices.length && (
        <RequiredMessage>Must have at least one option</RequiredMessage>
      )}
    </>
  );
});

const StateChoiceComponent = memo(function StateChoiceComponent({
  state,
  setState,
  stepSelect,
  handleFocus,
  canEditSettings,
  choice,
  index,
}: {
  state: ReentryState;
  setState: StepTypeDetailsProps<ReentryState>["setState"];
  stepSelect: StepTypeDetailsProps<ReentryState>["stepSelect"];
  handleFocus(focused: boolean): void;
  canEditSettings: boolean;
  choice: ReentryChoiceState;
  index: number;
}) {
  const setName = useHandler((name: string) =>
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.choices[index].name = name;
      }),
    ),
  );
  const setStep = useHandler((next: StepId | undefined) =>
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.choices[index].next = next;
      }),
    ),
  );
  const handleUp = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const choices = state.choices.splice(index, 1);
        state.choices.splice(index - 1, 0, ...choices);
      }),
    );
  });
  const handleDown = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const choices = state.choices.splice(index, 1);
        state.choices.splice(index + 1, 0, ...choices);
      }),
    );
  });
  const handleDelete = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        state.choices.splice(index, 1);
      }),
    );
  });
  return (
    <>
      <Divider />
      <LabeledInput
        label={
          <div className={multipleChoiceCss.label}>
            <div>Option #{index + 1}</div>
            {canEditSettings && (
              <>
                <IconButton
                  disabled={!index}
                  onClick={handleUp}
                  size={ButtonSize.SMALL}
                >
                  <LeftArrowIcon className={multipleChoiceCss.upArrow} />
                </IconButton>
                <IconButton
                  disabled={index === state.choices.length - 1}
                  onClick={handleDown}
                  size={ButtonSize.SMALL}
                >
                  <LeftArrowIcon className={multipleChoiceCss.downArrow} />
                </IconButton>
                <IconButton onClick={handleDelete} size={ButtonSize.SMALL}>
                  <TrashIcon />
                </IconButton>
              </>
            )}
          </div>
        }
      >
        <TextInput
          error={state.attempted && !choice.name}
          onChange={setName}
          onFocus={(focused: boolean) => {
            if (!choice.name) {
              handleFocus(focused);
            }
          }}
          readonly={!canEditSettings}
          theme={InputTheme.FORM}
          value={choice.name}
        />
      </LabeledInput>
      <LabeledInput
        errors={state.attempted && !choice.next ? ["Required"] : []}
        label={`Next step #${index + 1}`}
      >
        {stepSelect({ value: choice.next, valueChange: setStep })}
      </LabeledInput>
    </>
  );
});
